import { type FC, useEffect, useState, useRef } from "react"
import { Link, useLocation } from "react-router"
import styled, { css } from "styled-components"

import CommunityProfileAvatar from "@forento/shared-client/components/CommunityProfileAvatar"
import LexicalViewer from "@forento/shared-client/components/LexicalViewer"
import { type PersonalizedCommunityPost } from "@forento/shared/models/community"
import { toRelativeDateString } from "@forento/shared/utilities/date"
import { toCountFormat } from "@forento/shared/utilities/number"

import { useAlert } from "~/contexts/AlertContext"
import { usePlatform } from "~/contexts/PlatformContext"
import Button from "~/themes/school/components/Button"
import { CommentIcon, DeleteIcon, DotIcon, LikeIcon, StarIcon } from "~/themes/school/components/Icon"
import MoreActions from "~/themes/school/components/MoreActions"
import ProfilePopup from "~/themes/school/components/ProfilePopup"
import { fonts, lightTextColor } from "~/themes/school/styles"
import { useLocale, useTranslation } from "~/translations"
import routes from "~/utilities/routes"
import trpc from "~/utilities/trpc"

import Attachment from "./Attachment"
import PollOption from "./PollOption"
import useImageCarousel from "./useImageCarousel"

const longPostHeight = 250

type Props = {
	post: PersonalizedCommunityPost
	onChange(): void
	onPostContentLoaded?(postId: number, text: string): void
	showChannel?: boolean
}
const Post: FC<Props> = ({ post, onChange, onPostContentLoaded, showChannel }) => {
	const platform = usePlatform().platform
	const location = useLocation()
	const alert = useAlert()
	const locale = useLocale()
	const t = useTranslation()

	const [authorElement, setAuthorElement] = useState<HTMLElement | null>(null)
	const [postContentRef, setPostContentRef] = useState<HTMLDivElement | null>(null)
	const [isExpanded, setExpanded] = useState(false)
	const [showReadMore, setShowReadMore] = useState(false)
	const contentRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		if (postContentRef !== null && onPostContentLoaded) {
			onPostContentLoaded(post.id, postContentRef.textContent ?? "")
		}
	}, [onPostContentLoaded, postContentRef, post.id])

	useEffect(() => {
		if (postContentRef && contentRef.current) {
			const height = contentRef.current.scrollHeight
			setShowReadMore(height > longPostHeight)
		}
	}, [postContentRef, post.htmlContent])

	const handleVote = async (pollOptionId: number | null) => {
		await trpc.community.setPostVote.mutate({
			postId: post.id,
			pollOptionId:
				pollOptionId !== null && !post.pollOptions.find(x => x.id === pollOptionId)?.hasUserSelected
					? pollOptionId
					: null,
		})
		onChange()
	}

	const handleLike = async (reaction: boolean) => {
		if (reaction) {
			await trpc.community.createPostReaction.mutate(post.id)
		} else {
			await trpc.community.deletePostReaction.mutate(post.id)
		}
		onChange()
	}

	const handleDelete = async () => {
		const dialog = await alert.confirm(
			"Delete post",
			"Are you sure you want to delete this post? This action cannot be undone.",
		)
		if (!dialog.result) return
		await trpc.community.deletePost.mutate(post.id)
		dialog.close()
		onChange()
	}

	const link = location.pathname !== routes.community.post(post.id) ? routes.community.post(post.id) : undefined

	const categoryParts = []
	if (platform.content.multipleChannels && showChannel) {
		categoryParts.push(post.channel.name)
	}
	if (post.category) {
		categoryParts.push(post.category.label)
	}

	return (
		<Container>
			<ProfilePopup targetElement={authorElement} profileId={post.profile.id} />
			<Header>
				<Avatar profile={post.profile} ref={setAuthorElement} />
				<Author onClick={routes.community.profile(post.profile.id)}>
					{post.profile.firstName} {post.profile.lastName}
				</Author>
				<Date onClick={link}>
					{toRelativeDateString(post.createDate, locale)}
					{post.editDate && (
						<>
							<DotIcon className="h-1 w-1" />
							{t("community.post.editDate", { date: toRelativeDateString(post.editDate, locale) })}
						</>
					)}
				</Date>
				{post.hasEditAccess && (
					<MoreActions
						actions={[{ label: "Delete", icon: DeleteIcon, isDanger: true, onClick: handleDelete }]}
					/>
				)}
			</Header>
			{categoryParts.length > 0 && <Category>{categoryParts.join(" > ")}</Category>}
			{link !== undefined ? (
				<Link to={link} className="mx-2 mb-1 flex items-center">
					{post.pinIndex !== null && <StarIcon className="mr-2 h-6 w-6" />}
					<Title>{post.title}</Title>
				</Link>
			) : (
				<div className="mx-2 mb-1 flex items-center">
					{post.pinIndex !== null && <StarIcon className="mr-2 h-6 w-6" />}
					<Title>{post.title}</Title>
				</div>
			)}

			<ContentWrapper>
				<ContentContainer ref={contentRef} isExpanded={isExpanded} showReadMore={showReadMore}>
					<LexicalViewer ref={setPostContentRef} html={post.htmlContent} className="mx-2 mb-4" />
				</ContentContainer>

				{showReadMore && (
					<ReadMoreButton onClick={() => setExpanded(current => !current)}>
						{isExpanded ? t("community.post.readLess") : t("community.post.readMore")}
					</ReadMoreButton>
				)}
			</ContentWrapper>

			{post.images.length > 0 && <PostImages images={post.images} />}
			{post.pollOptions.length > 0 && (
				<PollOptions>
					{post.pollOptions.map((option, index) => (
						<PollOption
							key={index}
							option={option}
							onChange={isSelected => (isSelected ? handleVote(option.id) : handleVote(null))}
						/>
					))}
				</PollOptions>
			)}
			{post.attachments.length > 0 && (
				<Attachments>
					{post.attachments.map(attachment => (
						<Attachment key={attachment.id} attachment={attachment} />
					))}
				</Attachments>
			)}
			<Footer>
				{post.channel.userAccess === "react" ||
				post.channel.userAccess === "comment" ||
				post.channel.userAccess === "post" ? (
					<FooterGroup>
						<LikeButton onClick={() => handleLike(!post.hasUserReacted)}>
							<StyledLikeIcon isFilled={post.hasUserReacted} />
						</LikeButton>
						{toCountFormat(post.reactionsCount)}
					</FooterGroup>
				) : (
					<FooterGroup>
						<StyledLikeIcon />
						{toCountFormat(post.reactionsCount)}
					</FooterGroup>
				)}
				<FooterGroup as={Button} onClick={link}>
					<StyledCommentIcon />
					{toCountFormat(post.commentsCount)}
				</FooterGroup>
			</Footer>
		</Container>
	)
}

const PostImages: FC<{ images: { id: number; filePath: string }[] }> = ({ images }) => {
	const carousel = useImageCarousel(images)

	return (
		<div className="mb-4 flex flex-wrap gap-2">
			{carousel.element}
			{images.map((image, index) => (
				<Button onClick={() => carousel.showIndex(index)} key={image.id}>
					<img src={image.filePath} alt={`Post image ${index + 1}`} className="h-32 rounded-lg" />
				</Button>
			))}
		</div>
	)
}

const Container = styled.article`
	max-width: 1400px;
	background-color: #ffffff;
	box-shadow: 0px 4px 30px 0px rgba(223, 232, 255, 0.25);
	padding: 24px;
`

const Header = styled.div`
	display: grid;
	grid-template-areas: "avatar name actions" "avatar date actions";
	grid-template-columns: min-content 1fr min-content;
	gap: 0 16px;
	margin-bottom: 16px;
`

const Avatar = styled(CommunityProfileAvatar)`
	grid-area: avatar;
`

const Author = styled(Button)`
	grid-area: name;
	font-family: ${fonts.quicksand};
	font-size: 20px;
	font-weight: bold;
`

const Date = styled(Button)`
	grid-area: date;
	font-size: 14px;
	color: ${lightTextColor};
	display: flex;
	align-items: center;
	gap: 8px;
`

const Category = styled.p`
	font-size: 14px;
	color: ${lightTextColor};
	margin: 0 ${32 - 24}px 4px;
`

const Title = styled.h1`
	font-family: ${fonts.quicksand};
	font-weight: bold;
	font-size: 16px;
`

const PollOptions = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
	margin-bottom: 16px;
`

const Attachments = styled.div`
	font-size: 16px;
	color: ${lightTextColor};
	margin-bottom: 32px;
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
`

const Footer = styled.div`
	display: flex;
	align-items: center;
	gap: 42px;
	margin: 0 ${32 - 24}px;
`

const FooterGroup = styled.div`
	display: flex;
	align-items: center;
	font-size: 16px;
	gap: 12px;
`

const LikeButton = styled(Button)`
	display: flex;
	color: ${lightTextColor};
`

const StyledLikeIcon = styled(LikeIcon)`
	width: 24px;
	height: 24px;

	${props =>
		props.isFilled &&
		css`
			color: ${props => props.theme.primaryColor};
		`}
`

const StyledCommentIcon = styled(CommentIcon)`
	width: 24px;
	height: 24px;
`

const ContentWrapper = styled.div`
	position: relative;
`

const ContentContainer = styled.div<{ isExpanded: boolean; showReadMore: boolean }>`
	${props =>
		props.showReadMore &&
		!props.isExpanded &&
		css`
			max-height: ${longPostHeight}px;
			overflow: hidden;
			position: relative;

			&::after {
				content: "";
				position: absolute;
				bottom: 0;
				left: 0;
				right: 0;
				height: 60px;
				background: linear-gradient(transparent, white);
				pointer-events: none;
			}
		`}
`

const ReadMoreButton = styled(Button)`
	display: block;
	margin: 8px auto 16px;
	padding: 8px 16px;
	font-size: 14px;
	font-weight: 500;
	color: #374151;
	background: white;
	border: 1px solid #e5e7eb;
	border-radius: 6px;
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
	transition: all 0.2s ease;

	&:hover {
		background: #f9fafb;
		border-color: #d1d5db;
		transform: translateY(-1px);
		box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
	}
`

export default Post
